import { useRouteQuery } from '@vueuse/router'
import type {
  SearchOptions,
  SearchResponse,
  RequestOptions,
} from '@nuxtjs/algolia'
import type { Ref, ComputedRef } from 'vue'
import type { UseAlgoliaIndexSearchReturnType } from '~/composables/algolia/useAlgoliaIndexSearch'
import type { NuxtError } from 'nuxt/app'
import type { AsyncDataExecuteOptions } from '#app/composables/asyncData'
import type { AlgoliaEventItem } from '~/composables/types/algolia'
import { buildAlgoliaAnalyticsTags } from '~/utils/algoliaHelpers'
import { applyAlgoliaSearchAnalyticsGuard } from '~/utils/algoliaSearchAnalytics'

export const useTransactionalPageAlgoliaSearch = (
  algoliaSearch: Ref<UseAlgoliaIndexSearchReturnType<AlgoliaEventItem>>,
  currentIndexName: ComputedRef<string> | Ref<string> | string = ref(
    getEventListIndexName()
  ),
  algoliaSearchOptions?:
    | ComputedRef<RequestOptions & SearchOptions>
    | Ref<RequestOptions & SearchOptions>,
  hitsPerPagination: Ref<number | undefined> = ref(undefined)
) => {
  const {
    public: { appEnv },
  } = useRuntimeConfig()
  const searchqueryQuery = useRouteQuery('searchquery')

  const algoliaSearchOptionsValue = computed(() =>
    toValue(algoliaSearchOptions)
  )
  const currentIndexNameValue = computed(() => toValue(currentIndexName))

  const computedKey = computed(() => {
    const filters = algoliaSearchOptionsValue.value?.filters || ''
    const facetFilters = algoliaSearchOptionsValue.value?.facetFilters || []
    const page = algoliaSearchOptionsValue.value?.page || 0
    return `algoliaSearch-${currentIndexNameValue.value}-${searchqueryQuery.value}-${filters}-${JSON.stringify(facetFilters)}-page-${page}-${algoliaSearchOptions?.value?.aroundLatLngViaIP}`
  })

  return useAsyncData<SearchResponse<AlgoliaEventItem>>(
    computedKey.value,
    () => {
      const searchQuery =
        (algoliaSearchOptionsValue.value?.query as string) ||
        searchqueryQuery.value?.toString() ||
        ''

      return algoliaSearch.value.search({
        query: '',

        requestOptions: applyAlgoliaSearchAnalyticsGuard(
          {
            attributesToHighlight: [],

            facets: ['*'],
            hitsPerPage: hitsPerPagination.value,
            ...(algoliaSearchOptionsValue.value || {}),
            query: searchQuery,
            clickAnalytics: true,
            analyticsTags: buildAlgoliaAnalyticsTags(
              appEnv,
              algoliaSearchOptionsValue.value?.analyticsTags
            ),
          },
          searchQuery
        ),
      })
    }
  )
}

export const useTransactionalPageAlgoliaSearchSideEffects = (
  currentIndexName: Ref<string> = ref(getEventListIndexName()),
  loading: ComputedRef<boolean>,
  algoliaSearch: Ref<UseAlgoliaIndexSearchReturnType<AlgoliaEventItem>>,
  performAlgoliaSearch: (opts?: AsyncDataExecuteOptions) => Promise<void>,
  searchError: Ref<NuxtError<unknown> | null, NuxtError<unknown> | null>
) => {
  const loadingIndicator = useLoadingIndicator()
  watchEffect(() => {
    if (loading.value) {
      loadingIndicator.start()
    } else {
      loadingIndicator.finish()
    }
  })

  // Re-fetch when index name changes
  watch(
    () => currentIndexName.value,
    (value, oldValue) => {
      if (value !== oldValue) {
        // not sure why that type error happens, but this seems to make indexes update
        algoliaSearch.value = useAlgoliaIndexSearch(currentIndexName.value)
        performAlgoliaSearch()
      }
    },
    { immediate: false }
  )

  watchEffect(() => {
    if (searchError.value) {
      loadingIndicator.finish()
    }
  })
}
